import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DASHBOARD_THREAD_PARAM, chatHref } from "../utils/chatHref";
import { CHAT_HISTORY_UPDATED_EVENT, CHAT_STREAMING_STATUS_EVENT, type ChatHistoryEventDetail } from "../constants/events";
import { useResizablePanel } from "../hooks/useResizablePanel";
import { chatApi, ChatListItem } from "../services/chatApi";
import ChatHistorySection from "./ChatHistorySection";
import NavigationSection from "./NavigationSection";
import SidebarFooter from "./SidebarFooter";
import SidebarHeader from "./SidebarHeader";
import ResizeHandle from "./ResizeHandle";

const SIDEBAR_DEFAULT_WIDTH = 256; // matches Tailwind w-64
const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 480;
/*
 * Below this the sidebar starts collapsed. Picked at the tablet-landscape
 * boundary: at 820px (iPad portrait) an open sidebar took 62% of the viewport
 * and left 310px for the answer.
 */
const SIDEBAR_COLLAPSE_BREAKPOINT = 1024;

const Sidebar: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	/*
	 * The sidebar starts collapsed on a narrow viewport.
	 *
	 * It used to be open at every width, and its width is a pixel value rather
	 * than a fraction — so on an iPad in portrait (820px) it held 510px, 62% of
	 * the screen, leaving 310px for the answer and clipping every table. An
	 * analyst reviewing on a tablet got navigation and almost no content.
	 *
	 * The state follows the breakpoint only when the viewport CROSSES it, so a
	 * deliberate toggle in between survives instead of being overwritten by a
	 * handler firing on every resize tick.
	 */
	const [isOpen, setIsOpen] = useState(
		() =>
			typeof window === "undefined" ||
			window.innerWidth >= SIDEBAR_COLLAPSE_BREAKPOINT,
	);

	useEffect(() => {
		if (typeof window === "undefined" || !window.matchMedia) return;
		const query = window.matchMedia(
			`(min-width: ${SIDEBAR_COLLAPSE_BREAKPOINT}px)`,
		);
		const onCross = (e: MediaQueryListEvent) => setIsOpen(e.matches);
		query.addEventListener("change", onCross);
		return () => query.removeEventListener("change", onCross);
	}, []);

	const { width: sidebarWidth, isResizing, startResize } = useResizablePanel({
		initialWidth: SIDEBAR_DEFAULT_WIDTH,
		minWidth: SIDEBAR_MIN_WIDTH,
		maxWidth: SIDEBAR_MAX_WIDTH,
		edge: "right",
	});
	const [chatHistoryExpanded, setChatHistoryExpanded] = useState(false);
	const hasAutoExpandedChatHistoryRef = useRef(false);
	const [chatItems, setChatItems] = useState<ChatListItem[]>([]);
	const [loadingChats, setLoadingChats] = useState(false);
	const [loadingMoreChats, setLoadingMoreChats] = useState(false);
	const [chatHistoryError, setChatHistoryError] = useState<string | null>(null);
	const [chatHistoryStale, setChatHistoryStale] = useState(false);
	const [chatDeleteError, setChatDeleteError] = useState<string | null>(null);
	const [hasResolvedInitialChatPage, setHasResolvedInitialChatPage] =
		useState(false);
	const [chatNextCursor, setChatNextCursor] = useState<string | null>(null);
	const [chatHasMore, setChatHasMore] = useState(false);
	const [streamingChatIds, setStreamingChatIds] = useState<ReadonlySet<string>>(
		() => new Set<string>(),
	);
	// Tracks streamToken of the in-view send so we only clear *its* entry on a
	// "done" event; background streams continue to be reconciled from list
	// responses (has_active_stream) instead.
	const activeStreamTokenByChatRef = useRef<Map<string, number>>(new Map());
	const isFetchingChatsRef = useRef(false);
	// A refresh asked for while a first-page fetch is in flight. That fetch
	// may have left before the change it was asked for (a rail thread's
	// surface_state is written at stream start), so one more runs after it
	// rather than the request being dropped.
	const pendingChatRefetchRef = useRef(false);
	const fetchChatItemsRef = useRef<
		((cursor?: string | null, options?: { preserveOnError?: boolean }) => Promise<void>) | null
	>(null);
	const isFetchingMoreChatsRef = useRef(false);
	const chatHistoryRefreshTimerRef = useRef<number | null>(null);
	// Auto-dismiss timer for the inline "could not delete chat" error.
	const chatDeleteErrorTimerRef = useRef<number | null>(null);
	// IDs of chats inserted via optimistic "upsert" events that the server's
	// first-page response has not yet acknowledged. Anything in this set is
	// preserved across background refetches; everything else defers to server
	// truth (so deletes propagate and load-more pages don't get re-merged).
	const optimisticChatIdsRef = useRef<Set<string>>(new Set());
	const chatItemsRef = useRef<ChatListItem[]>([]);
	useEffect(() => {
		chatItemsRef.current = chatItems;
	}, [chatItems]);

	const activeChatId = useMemo(() => {
		const match = location.pathname.match(/^\/chat\/([^/]+)$/);
		if (match) {
			return match[1] ?? null;
		}
		// A workflow thread is open in the Analyst rail, named by the URL. Any
		// workflow: the rail is a property of the shell, not of one route
		// (DOP-478), so this matches the section rather than a single path.
		if (location.pathname.startsWith("/workflows")) {
			return (
				new URLSearchParams(location.search ?? "").get(DASHBOARD_THREAD_PARAM) ??
				null
			);
		}
		return null;
	}, [location.pathname, location.search]);

	const toggleSidebar = () => {
		setIsOpen(!isOpen);
	};

	// Clear the pending delete-error dismiss timer on unmount.
	useEffect(() => {
		return () => {
			if (chatDeleteErrorTimerRef.current !== null) {
				window.clearTimeout(chatDeleteErrorTimerRef.current);
				chatDeleteErrorTimerRef.current = null;
			}
		};
	}, []);

	const fetchChatItems = useCallback(
		async (
			cursor?: string | null,
			options?: { preserveOnError?: boolean },
		) => {
			const isLoadMoreRequest = Boolean(cursor);
			const preserveOnError = options?.preserveOnError ?? false;
			if (isLoadMoreRequest) {
				if (isFetchingMoreChatsRef.current) {
					return;
				}
				isFetchingMoreChatsRef.current = true;
				setLoadingMoreChats(true);
			} else {
				if (isFetchingChatsRef.current) {
					pendingChatRefetchRef.current = true;
					return;
				}
				isFetchingChatsRef.current = true;
				setLoadingChats(true);
			}

			try {
				const response = await chatApi.listChats({
					limit: 20,
					cursor: cursor ?? undefined,
				});

				// A loan tape thread IS a workflow (DOP-477): it is listed under
				// Workflows above, and the backend already keeps it out of this
				// list. Dropped here too so a future list that stops filtering
				// cannot quietly show every workflow twice.
				const chats = (Array.isArray(response.chats) ? response.chats : [])
					.filter((chat) => chat.surface !== "loan_tape");
				setChatItems((prev) => {
					if (isLoadMoreRequest) {
						return [...prev, ...chats];
					}
					// Preserve only chats explicitly inserted via optimistic upsert
					// that the server hasn't yet acknowledged. Drop them from the
					// optimistic set once the server confirms them — at that point
					// they're regular server-tracked rows.
					const serverIds = new Set(chats.map((c) => c.id));
					const stillPending: ChatListItem[] = [];
					for (const id of optimisticChatIdsRef.current) {
						if (serverIds.has(id)) {
							optimisticChatIdsRef.current.delete(id);
							continue;
						}
						const local = prev.find((c) => c.id === id);
						if (local) stillPending.push(local);
					}
					return [...stillPending, ...chats];
				});
				setChatNextCursor(response.next_cursor);
				setChatHasMore(response.has_more);
				setChatHistoryError(null);
				setChatHistoryStale(false);
				// Reconcile streaming indicators from server truth for the chats
				// present in this response. Handles the case where a stream
				// finishes while the user is on another chat — no event fires on
				// the view that started it, so the set is only refreshed via this
				// path (or the next CHAT_STREAMING_STATUS_EVENT).
				setStreamingChatIds((prev) => {
					const next = new Set(prev);
					let changed = false;
					for (const chat of chats) {
						const shouldStream = Boolean(chat.has_active_stream);
						const currentlyStreaming = next.has(chat.id);
						if (shouldStream && !currentlyStreaming) {
							next.add(chat.id);
							changed = true;
						} else if (!shouldStream && currentlyStreaming) {
							next.delete(chat.id);
							activeStreamTokenByChatRef.current.delete(chat.id);
							changed = true;
						}
					}
					return changed ? next : prev;
				});
				if (!isLoadMoreRequest) {
					setHasResolvedInitialChatPage(true);
				}
			} catch (error) {
				console.error("Error fetching chat history:", error);

				if (!isLoadMoreRequest) {
					// preserveOnError only applies when we have something to preserve;
					// otherwise the user would see "No chats yet" with no signal that
					// the fetch actually failed.
					if (preserveOnError && chatItemsRef.current.length > 0) {
						setChatHistoryStale(true);
					} else {
						setChatHistoryError("Unable to load chat history");
						setChatItems([]);
						setChatNextCursor(null);
						setChatHasMore(false);
					}
				} else {
					setChatHistoryError("Unable to load chat history");
				}
			} finally {
				if (isLoadMoreRequest) {
					isFetchingMoreChatsRef.current = false;
					setLoadingMoreChats(false);
				} else {
					isFetchingChatsRef.current = false;
					setLoadingChats(false);
					if (pendingChatRefetchRef.current) {
						pendingChatRefetchRef.current = false;
						void fetchChatItemsRef.current?.(undefined, { preserveOnError: true });
					}
				}
			}
		},
		[],
	);
	fetchChatItemsRef.current = fetchChatItems;

	const toggleChatHistory = () => {
		setChatHistoryExpanded((prev) => {
			const next = !prev;
			if (
				next &&
				chatItems.length === 0 &&
				!loadingChats &&
				!(hasResolvedInitialChatPage && !chatHasMore)
			) {
				void fetchChatItems();
			}
			return next;
		});
	};

	const handleChatSelect = (chatId: string) => {
		const chat = chatItems.find((item) => item.id === chatId);
		navigate(chatHref(chat ?? { id: chatId }));
	};

	const handleLoadMoreChats = () => {
		if (!chatNextCursor || loadingMoreChats) {
			return;
		}
		void fetchChatItems(chatNextCursor);
	};

	const handleRenameChat = useCallback(
		async (chatId: string, title: string) => {
			// Optimistically update UI first
			setChatItems((prev) =>
				prev.map((c) => (c.id === chatId ? { ...c, title } : c)),
			);
			try {
				await chatApi.renameChat(chatId, title);
			} catch (error) {
				console.error("Error renaming chat:", error);
				// Revert on failure by re-fetching
				void fetchChatItems();
			}
		},
		[fetchChatItems],
	);

	const handleDeleteChat = useCallback(
		async (chatId: string) => {
			// Clear any prior delete error before a fresh attempt.
			if (chatDeleteErrorTimerRef.current !== null) {
				window.clearTimeout(chatDeleteErrorTimerRef.current);
				chatDeleteErrorTimerRef.current = null;
			}
			setChatDeleteError(null);
			try {
				await chatApi.deleteChat(chatId);
				optimisticChatIdsRef.current.delete(chatId);
				setChatItems((prev) => {
					const updated = prev.filter((c) => c.id !== chatId);
					if (activeChatId === chatId) {
						// Navigate to the next chat in the list (the one that was below),
						// or the previous one if we deleted the last item, or /chat if empty.
						const deletedIndex = prev.findIndex((c) => c.id === chatId);
						const nextChat =
							updated[deletedIndex] ?? updated[deletedIndex - 1];
						navigate(nextChat ? `/chat/${nextChat.id}` : "/chat");
					}
					return updated;
				});
			} catch (error) {
				// The chat stays in chatItems on failure; surface a user-visible
				// error instead of leaving the failed delete looking like a glitch.
				console.error("Error deleting chat:", error);
				setChatDeleteError("Could not delete chat. Please try again.");
				chatDeleteErrorTimerRef.current = window.setTimeout(() => {
					setChatDeleteError(null);
					chatDeleteErrorTimerRef.current = null;
				}, 5000);
			}
		},
		[activeChatId, navigate],
	);

	useEffect(() => {
		if (
			chatItems.length === 0 &&
			!loadingChats &&
			!(hasResolvedInitialChatPage && !chatHasMore) &&
			!chatHistoryError
		) {
			void fetchChatItems();
		}
	}, [
		hasResolvedInitialChatPage,
		chatHasMore,
		chatHistoryError,
		chatItems.length,
		fetchChatItems,
		loadingChats,
	]);

	// Auto-expand the chat history section once the initial fetch resolves
	// (success or empty). Only fires once, so a manual collapse afterward sticks.
	useEffect(() => {
		if (hasResolvedInitialChatPage && !hasAutoExpandedChatHistoryRef.current) {
			hasAutoExpandedChatHistoryRef.current = true;
			setChatHistoryExpanded(true);
		}
	}, [hasResolvedInitialChatPage]);

	useEffect(() => {
		const scheduleRefetch = () => {
			if (chatHistoryRefreshTimerRef.current !== null) {
				window.clearTimeout(chatHistoryRefreshTimerRef.current);
			}
			chatHistoryRefreshTimerRef.current = window.setTimeout(() => {
				chatHistoryRefreshTimerRef.current = null;
				void fetchChatItems(undefined, { preserveOnError: true });
			}, 300);
		};

		const handleChatHistoryUpdated = (e: Event) => {
			const detail = (e as CustomEvent<ChatHistoryEventDetail | undefined>)
				.detail;

			if (!detail) {
				scheduleRefetch();
				return;
			}

			if (detail.kind === "upsert") {
				const incoming = detail.chat;
				setChatItems((prev) => {
					const existingIndex = prev.findIndex((c) => c.id === incoming.id);
					if (existingIndex === -1) {
						optimisticChatIdsRef.current.add(incoming.id);
						return [incoming, ...prev];
					}
					// Move-to-top + merge: preserve fields not present on incoming
					// (e.g. server-derived has_active_stream) by spreading existing
					// first, then overlaying incoming.
					const existing = prev[existingIndex];
					const merged = { ...existing, ...incoming };
					const without = prev.filter((_, i) => i !== existingIndex);
					return [merged, ...without];
				});
				return;
			}

			if (detail.kind === "title") {
				setChatItems((prev) =>
					prev.map((c) =>
						c.id === detail.chatId ? { ...c, title: detail.title } : c,
					),
				);
				return;
			}
		};

		window.addEventListener(CHAT_HISTORY_UPDATED_EVENT, handleChatHistoryUpdated);
		return () => {
			if (chatHistoryRefreshTimerRef.current !== null) {
				window.clearTimeout(chatHistoryRefreshTimerRef.current);
				chatHistoryRefreshTimerRef.current = null;
			}
			window.removeEventListener(
				CHAT_HISTORY_UPDATED_EVENT,
				handleChatHistoryUpdated,
			);
		};
	}, [fetchChatItems]);

	useEffect(() => {
		const handleStreamingStatus = (e: Event) => {
			const detail = (e as CustomEvent).detail;
			const id = typeof detail?.chatId === "string" ? detail.chatId : null;
			if (!id) return;
			const token = detail?.streamToken as number | undefined;
			if (detail?.isStreaming) {
				if (typeof token === "number") {
					activeStreamTokenByChatRef.current.set(id, token);
				}
				setStreamingChatIds((prev) => {
					if (prev.has(id)) return prev;
					const next = new Set(prev);
					next.add(id);
					return next;
				});
			} else {
				// Only clear when the ending token matches the one we recorded on
				// start. Mismatched tokens (e.g. from a second concurrent send or a
				// stale event) must not clear a stream that is still live.
				const recorded = activeStreamTokenByChatRef.current.get(id);
				if (recorded !== (token ?? undefined)) return;
				activeStreamTokenByChatRef.current.delete(id);
				setStreamingChatIds((prev) => {
					if (!prev.has(id)) return prev;
					const next = new Set(prev);
					next.delete(id);
					return next;
				});
			}
		};
		window.addEventListener(CHAT_STREAMING_STATUS_EVENT, handleStreamingStatus);
		return () => {
			window.removeEventListener(CHAT_STREAMING_STATUS_EVENT, handleStreamingStatus);
		};
	}, []);

	return (
		<div
			className={`relative ${isOpen ? "" : "w-[72px]"} bg-[#f7f7f6] border-r border-[#e7e7e4] flex flex-col h-full ${isResizing ? "" : "transition-[width] duration-200 ease-in-out"} overflow-hidden`}
			style={isOpen ? { width: sidebarWidth } : undefined}
		>
			{isOpen && (
				<ResizeHandle
					edge="right"
					isResizing={isResizing}
					onPointerDown={startResize}
					label="Resize Deal Room sidebar"
				/>
			)}
			<SidebarHeader isOpen={isOpen} onToggle={toggleSidebar} />
			<NavigationSection isOpen={isOpen} />

			<div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-2 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
				{isOpen && (
					<div className="space-y-4">
						<ChatHistorySection
							isExpanded={chatHistoryExpanded}
							onToggle={toggleChatHistory}
							chats={chatItems}
							loading={loadingChats}
							loadingMore={loadingMoreChats}
							error={chatHistoryError}
							deleteError={chatDeleteError}
							stale={chatHistoryStale}
							activeChatId={activeChatId}
							streamingChatIds={streamingChatIds}
							hasMore={chatHasMore}
							onChatSelect={handleChatSelect}
							onLoadMore={handleLoadMoreChats}
							onDeleteChat={handleDeleteChat}
							onRenameChat={handleRenameChat}
						/>
					</div>
				)}
			</div>

			<SidebarFooter isOpen={isOpen} />
		</div>
	);
};

export default Sidebar;
